import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography,
} from '@mui/material'
import AddIcon          from '@mui/icons-material/Add'
import CheckCircleIcon  from '@mui/icons-material/CheckCircle'
import CancelIcon       from '@mui/icons-material/Cancel'
import EventIcon        from '@mui/icons-material/Event'
import PhoneIcon        from '@mui/icons-material/Phone'
import GroupsIcon       from '@mui/icons-material/Groups'
import PresentToAllIcon from '@mui/icons-material/PresentToAll'
import LocationOnIcon   from '@mui/icons-material/LocationOn'
import MoreHorizIcon    from '@mui/icons-material/MoreHoriz'
import AccessTimeIcon   from '@mui/icons-material/AccessTime'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { retentionApi, RetentionFollowUp, CreateRetentionFollowUpPayload } from '../../lib/api/retentionApi'
import { themeColors } from '../../app/layout/theme'
import { EntityModal } from '../shared/EntityModal'
import { FormInput, FormSelect } from '../ui'
import { toast } from 'react-toastify'

const getLocalDateTimeString = () => {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

const EVENT_TYPES = [
  { value: 'CALL',       label: 'Call'       },
  { value: 'MEETING',    label: 'Meeting'    },
  { value: 'DEMO',       label: 'Demo'       },
  { value: 'SITE_VISIT', label: 'Site Visit' },
  { value: 'OTHER',      label: 'Other'      },
]

function typeIcon(type: string, size = 16) {
  const props = { sx: { fontSize: size } }
  switch (type) {
    case 'CALL':       return <PhoneIcon {...props} />
    case 'MEETING':    return <GroupsIcon {...props} />
    case 'DEMO':       return <PresentToAllIcon {...props} />
    case 'SITE_VISIT': return <LocationOnIcon {...props} />
    default:           return <MoreHorizIcon {...props} />
  }
}

function typeMeta(type: string): { color: string; bg: string } {
  switch (type) {
    case 'CALL':       return { color: '#0070d2', bg: '#e8f4fd' }
    case 'MEETING':    return { color: '#7b1fa2', bg: '#f3e5f5' }
    case 'DEMO':       return { color: '#00838f', bg: '#e0f7fa' }
    case 'SITE_VISIT': return { color: '#2e7d32', bg: '#e8f5e9' }
    default:           return { color: '#f57c00', bg: '#fff3e0' }
  }
}

function fmtDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + '  •  ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

function isOverdue(ev: RetentionFollowUp) {
  return ev.status === 'PENDING' && new Date(ev.scheduledAt) < new Date()
}

// ─── Create Event Modal ──────────────────────────────────────────────────────

function CreateEventModal({ open, onClose, onCreate, isCreating }: {
  open: boolean; onClose: () => void; onCreate: (p: CreateRetentionFollowUpPayload) => void; isCreating: boolean
}) {
  const [title,    setTitle]    = useState('')
  const [type,     setType]     = useState<string>('MEETING')
  const [date,     setDate]     = useState(getLocalDateTimeString())
  const [endDate,  setEndDate]  = useState('')
  const [notes,    setNotes]    = useState('')
  const [reminder, setReminder] = useState<string>('')

  function reset() { setTitle(''); setType('MEETING'); setDate(getLocalDateTimeString()); setEndDate(''); setNotes(''); setReminder('') }
  function handleClose() { reset(); onClose() }

  function handleSubmit() {
    if (!date) return
    if (endDate && new Date(endDate) < new Date(date)) { toast.error('End time cannot be earlier than start time'); return }
    const remVal  = typeof reminder === 'object' && reminder !== null ? (reminder as any).value : reminder
    const typeVal = typeof type    === 'object' && type    !== null ? (type    as any).value : type
    onCreate({
      title: title.trim() || undefined,
      type: typeVal,
      scheduledAt: new Date(date).toISOString(),
      endTime: endDate ? new Date(endDate).toISOString() : undefined,
      notes: notes.trim() || undefined,
      reminderMinutes: remVal ? Number(remVal) : undefined,
    })
  }

  const REMINDER_OPTIONS = [
    { value: '',     label: 'No reminder'       },
    { value: '5',    label: '5 minutes before'  },
    { value: '10',   label: '10 minutes before' },
    { value: '15',   label: '15 minutes before' },
    { value: '30',   label: '30 minutes before' },
    { value: '60',   label: '1 hour before'     },
    { value: '120',  label: '2 hours before'    },
    { value: '1440', label: '1 day before'      },
  ]

  return (
    <EntityModal open={open} onClose={handleClose} onSubmit={handleSubmit} title="Schedule Event" submitLabel="Schedule"
      isSubmitting={isCreating} submitDisabled={!date || new Date(date) < new Date(getLocalDateTimeString())} maxWidth="sm"
      icon={<EventIcon sx={{ fontSize: 18 }} />}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormInput label="Event Title" placeholder="e.g. Follow-up call with customer" value={title} onChange={e => setTitle(e.target.value)} fullWidth />
        <FormSelect value={EVENT_TYPES.find(t => t.value === type) || type} label="Type"
          onChange={v => { const val = typeof v === 'object' && v !== null ? (v as any).value : v; setType(val) }}
          options={EVENT_TYPES} fullWidth />
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <FormInput label="Start Date & Time" type="datetime-local" value={date} onChange={e => setDate(e.target.value)}
            fullWidth required InputLabelProps={{ shrink: true }} inputProps={{ min: getLocalDateTimeString() }} />
          <FormInput label="End Time (optional)" type="datetime-local" value={endDate} onChange={e => setEndDate(e.target.value)}
            fullWidth InputLabelProps={{ shrink: true }} inputProps={{ min: date }} />
        </Box>
        <FormSelect value={reminder} label="Reminder" onChange={v => setReminder(v as string)} options={REMINDER_OPTIONS} fullWidth />
        <FormInput label="Notes" placeholder="Add notes or agenda…" value={notes} onChange={e => setNotes(e.target.value)} fullWidth multiline minRows={2} />
      </Box>
    </EntityModal>
  )
}

// ─── Complete Event Modal ────────────────────────────────────────────────────

function CompleteEventModal({ open, event, onClose, onComplete, isCompleting }: {
  open: boolean; event: RetentionFollowUp | null; onClose: () => void; onComplete: (fuId: string, outcome: string) => void; isCompleting: boolean
}) {
  const [outcome, setOutcome] = useState('')
  function handleClose() { setOutcome(''); onClose() }
  function handleSubmit() { if (!event) return; onComplete(event.id, outcome); setOutcome('') }

  return (
    <EntityModal open={open} onClose={handleClose} onSubmit={handleSubmit} title="Mark Event as Done" submitLabel="Mark Done"
      isSubmitting={isCompleting} icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}>
      <Typography variant="body2" sx={{ color: '#374151', mb: 1.5 }}>
        Briefly describe the outcome of <strong>{event?.title ?? event?.type}</strong>:
      </Typography>
      <FormInput placeholder="e.g. Customer agreed to place an order next week…" value={outcome} onChange={e => setOutcome(e.target.value)} fullWidth multiline minRows={3} />
    </EntityModal>
  )
}

// ─── Single Event Card ───────────────────────────────────────────────────────

function EventCard({ event, onComplete, onCancel, isMutating }: {
  event: RetentionFollowUp; onComplete: (ev: RetentionFollowUp) => void; onCancel: (ev: RetentionFollowUp) => void; isMutating: boolean
}) {
  const meta     = typeMeta(event.type)
  const overdue  = isOverdue(event)
  const isDone   = event.status === 'DONE'
  const isCancld = event.status === 'CANCELLED'

  const statusColor = isDone ? '#16a34a' : isCancld ? '#9ca3af' : overdue ? '#dc2626' : '#f59e0b'
  const statusLabel = isDone ? 'Done' : isCancld ? 'Cancelled' : overdue ? 'Overdue' : 'Upcoming'

  return (
    <Box sx={{ display: 'flex', gap: 2, position: 'relative', '&:not(:last-child)::before': { content: '""', position: 'absolute', left: 19, top: 42, bottom: -20, width: 2, bgcolor: '#e5e7eb', zIndex: 0 } }}>
      <Box sx={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, bgcolor: meta.bg, color: meta.color, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${meta.color}30` }}>
        {typeIcon(event.type, 18)}
      </Box>

      <Box sx={{ flex: 1, pb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827', lineHeight: 1.3 }}>
              {event.title ?? EVENT_TYPES.find(t => t.value === event.type)?.label ?? event.type}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
              <AccessTimeIcon sx={{ fontSize: 12, color: '#9ca3af' }} />
              <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                {fmtDate(event.scheduledAt)}
                {event.endTime && ` → ${fmtTime(event.endTime)}`}
              </Typography>
            </Box>
            {event.reminderMinutes != null && (
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.3 }}>🔔 Reminder {event.reminderMinutes} min before</Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Chip label={statusLabel} size="small" sx={{ fontWeight: 600, fontSize: 11, height: 22, bgcolor: `${statusColor}15`, color: statusColor, border: `1px solid ${statusColor}30` }} />
            {event.status === 'PENDING' && (
              <>
                <Tooltip title="Mark as Done">
                  <span>
                    <IconButton size="small" disabled={isMutating} onClick={() => onComplete(event)} sx={{ color: '#16a34a', '&:hover': { bgcolor: '#dcfce7' } }}>
                      <CheckCircleIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Cancel">
                  <span>
                    <IconButton size="small" disabled={isMutating} onClick={() => onCancel(event)} sx={{ color: '#9ca3af', '&:hover': { bgcolor: '#f3f4f6' } }}>
                      <CancelIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        {overdue && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, bgcolor: '#fef2f2', borderRadius: '6px', px: 1.5, py: 0.5, border: '1px solid #fecaca' }}>
            <WarningAmberIcon sx={{ fontSize: 14, color: '#dc2626' }} />
            <Typography sx={{ fontSize: 12, color: '#dc2626', fontWeight: 500 }}>This event is overdue</Typography>
          </Box>
        )}

        {event.notes && (
          <Box sx={{ mt: 1, bgcolor: '#f9fafb', borderRadius: '8px', px: 1.5, py: 1, border: '1px solid #f3f4f6' }}>
            <Typography sx={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{event.notes}</Typography>
          </Box>
        )}

        {isDone && event.outcome && (
          <Box sx={{ mt: 1, bgcolor: '#f0fdf4', borderRadius: '8px', px: 1.5, py: 1, border: '1px solid #bbf7d0', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <CheckCircleIcon sx={{ fontSize: 15, color: '#16a34a', mt: '1px', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, color: '#166534' }}><strong>Outcome:</strong> {event.outcome}</Typography>
          </Box>
        )}

        {event.assignedTo && (
          <Typography sx={{ fontSize: 12, color: '#9ca3af', mt: 0.75 }}>
            Assigned to <strong style={{ color: '#6b7280' }}>{event.assignedTo.name}</strong>
          </Typography>
        )}
      </Box>
    </Box>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

interface Props {
  retentionId: string
  canModify: boolean
  externalCreateOpen?: boolean
  onExternalCreateClose?: () => void
  hideToolbar?: boolean
}

const RetentionEventsSection = observer(function RetentionEventsSection({ retentionId, canModify, externalCreateOpen, onExternalCreateClose, hideToolbar }: Props) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen]   = useState(false)
  const [completeEv, setCompleteEv]   = useState<RetentionFollowUp | null>(null)

  const isCreateOpen = createOpen || (externalCreateOpen ?? false)
  const handleCreateClose = () => { setCreateOpen(false); onExternalCreateClose?.() }

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['retention-follow-ups', retentionId],
    queryFn: () => retentionApi.getFollowUps(retentionId),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['retention-follow-ups', retentionId] })
    qc.invalidateQueries({ queryKey: ['retention-activities', retentionId] })
  }

  const createMut = useMutation({
    mutationFn: (p: CreateRetentionFollowUpPayload) => retentionApi.addFollowUp(retentionId, p),
    onSuccess: () => { invalidate(); handleCreateClose(); toast.success('Event scheduled') },
    onError:   (err: any) => toast.error(err?.response?.data?.message || 'Failed to schedule event'),
  })

  const completeMut = useMutation({
    mutationFn: ({ fuId, outcome }: { fuId: string; outcome: string }) => retentionApi.completeFollowUp(retentionId, fuId, outcome),
    onSuccess: () => { invalidate(); setCompleteEv(null) },
  })

  const cancelMut = useMutation({
    mutationFn: (fuId: string) => retentionApi.cancelFollowUp(retentionId, fuId),
    onSuccess: invalidate,
  })

  const isMutating = completeMut.isPending || cancelMut.isPending

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>

  return (
    <Box>
      {canModify && !hideToolbar && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, background: themeColors.gradient?.blue ?? themeColors.primary, fontSize: 13 }}>
            Add Event
          </Button>
        </Box>
      )}

      {events.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <EventIcon sx={{ fontSize: 48, color: '#e5e7eb', mb: 1 }} />
          <Typography sx={{ color: '#9ca3af', fontWeight: 500 }}>No events scheduled</Typography>
          <Typography sx={{ color: '#d1d5db', fontSize: 13 }}>Schedule follow-up calls, meetings, or site visits</Typography>
        </Box>
      )}

      {events.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          {events.map(ev => (
            <EventCard key={ev.id} event={ev} onComplete={setCompleteEv} onCancel={ev2 => cancelMut.mutate(ev2.id)} isMutating={isMutating} />
          ))}
        </Box>
      )}

      <CreateEventModal open={isCreateOpen} onClose={handleCreateClose} onCreate={p => createMut.mutate(p)} isCreating={createMut.isPending} />
      <CompleteEventModal open={completeEv !== null} event={completeEv} onClose={() => setCompleteEv(null)} onComplete={(fuId, outcome) => completeMut.mutate({ fuId, outcome })} isCompleting={completeMut.isPending} />
    </Box>
  )
})

export default RetentionEventsSection
